import React, { useMemo } from 'react';
import IndicatorSelector from './IndicatorSelector';
import { Input } from '../../ui/input';
import { TIMEFRAMES } from '../../../lib/timeframes';

const ARITH_OPS = ['ADD','SUB','MUL','DIV','+','-','*','/'];
const OP_LABELS = [
  { value: 'ADD', label: 'ADD (+)' },
  { value: 'SUB', label: 'SUB (-)' },
  { value: 'MUL', label: 'MUL (×)' },
  { value: 'DIV', label: 'DIV (÷)' },
];

function normalizeOp(op) {
  if (!op) return 'ADD';
  const s = String(op).toUpperCase();
  if (s === '+') return 'ADD';
  if (s === '-') return 'SUB';
  if (s === '*') return 'MUL';
  if (s === '/') return 'DIV';
  if (['ADD','SUB','MUL','DIV'].includes(s)) return s;
  return 'ADD';
}

function detectOperandMode(opnd) {
  if (opnd == null) return 'number';
  if (typeof opnd === 'number') return 'number';
  if (typeof opnd === 'object') {
    if (opnd.op && opnd.lhs !== undefined && opnd.rhs !== undefined && ARITH_OPS.includes(String(opnd.op).toUpperCase())) {
      return 'math';
    }
    // Treat indicator PRICE as indicator mode; explicit price stays price
    if (opnd.type === 'price') return 'price';
    if (opnd.type === 'indicator') return 'indicator';
  }
  return 'number';
}

function PriceEditor({ value, onChange, baseTimeframe }) {
  const v = value && value.type === 'price' ? value : { type: 'price', source: 'close' };
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <label className="text-xs text-textSecondary w-16">Field</label>
        <select
          className="h-8 text-xs rounded border bg-backgroundSurface text-textPrimary w-28"
          value={v.source || 'close'}
          onChange={(e)=>onChange({ ...v, source: e.target.value })}
        >
          {['open','high','low','close','volume'].map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-textSecondary w-16">TF</label>
        <select
          className="h-8 text-xs rounded border bg-backgroundSurface text-textPrimary w-28"
          value={v.timeframe || ''}
          onChange={(e)=>onChange({ ...v, timeframe: e.target.value || undefined })}
        >
          <option value="">Base ({baseTimeframe || 'M1'})</option>
          {TIMEFRAMES.map(tf => <option key={tf} value={tf}>{tf}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-textSecondary w-16">Lag</label>
        <input
          type="number"
          min={0}
          step={1}
          className="h-8 text-xs rounded border bg-backgroundSurface text-textPrimary w-20 px-2"
          value={Number.isFinite(v.lag) ? v.lag : 0}
          onChange={(e)=>onChange({ ...v, lag: Math.max(0, parseInt(e.target.value || '0',10)) })}
        />
      </div>
    </div>
  );
}

function OperandEditor({ value, onChange, baseTimeframe, compact = false }) {
  const mode = useMemo(() => detectOperandMode(value), [value]);

  const setMode = (m) => {
    if (m === mode) return;
    if (m === 'number') onChange(0);
    else if (m === 'price') onChange({ type: 'price', source: 'close' });
    else if (m === 'indicator') onChange({ type: 'indicator', name: 'ema', params: { length: 21 }, input: { type: 'price', source: 'close' } });
    else if (m === 'math') onChange({ op: 'SUB', lhs: { type: 'price', source: 'close' }, rhs: 0 });
  };

  return (
    <div className={compact ? '' : 'space-y-2'}>
      <div className="inline-flex items-center p-0.5 bg-muted rounded-md mb-2">
        {[
          { label: 'Number', value: 'number' },
          { label: 'Price', value: 'price' },
          { label: 'Indicator', value: 'indicator' },
          { label: 'Math', value: 'math' },
        ].map(o => (
          <button
            key={o.value}
            type="button"
            onClick={()=>setMode(o.value)}
            className={`px-2 py-0.5 text-xs rounded ${mode === o.value ? 'bg-backgroundSurface text-textPrimary shadow-sm' : 'text-textSecondary'}`}
          >{o.label}</button>
        ))}
      </div>

      {mode === 'number' && (
        <Input
          type="number"
          className="h-8 w-36 text-right"
          value={typeof value === 'number' ? value : 0}
          onChange={(e)=>onChange(parseFloat(e.target.value || '0'))}
        />
      )}

      {mode === 'price' && (
        <PriceEditor value={value} onChange={onChange} baseTimeframe={baseTimeframe} />
      )}

      {mode === 'indicator' && (
        <div className="max-w-[75%]">
          <IndicatorSelector dense value={value && value.type==='indicator' ? value : { type:'indicator', name:'ema', params:{ length:21 }, input:{ type:'price', source:'close' } }} onChange={onChange} baseTimeframe={baseTimeframe} />
        </div>
      )}

      {mode === 'math' && (
        <MathNode value={value && typeof value==='object' ? value : { op:'SUB', lhs: { type:'price', source:'close' }, rhs: 0 }} onChange={onChange} baseTimeframe={baseTimeframe} compact />
      )}
    </div>
  );
}

export default function MathNode({ value, onChange, baseTimeframe, compact = false }) {
  const v = value && typeof value === 'object' ? value : { op: 'ADD', lhs: 0, rhs: 0 };
  const op = normalizeOp(v.op);
  const setOp = (o) => onChange({ ...v, op: o });
  const setLhs = (lhs) => onChange({ ...v, lhs });
  const setRhs = (rhs) => onChange({ ...v, rhs });

  return (
    <div className={`rounded-lg border border-borderDefault ${compact? 'bg-transparent p-2' : 'bg-backgroundSurface/50 p-3'} space-y-2`}>
      <div className="flex items-center gap-2">
        <label className="text-xs text-textSecondary">Operation</label>
        <select
          className="h-8 text-xs rounded-md border bg-backgroundSurface"
          value={op}
          onChange={(e)=>setOp(normalizeOp(e.target.value))}
        >
          {OP_LABELS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-textSecondary mb-1">Left</div>
          <OperandEditor value={v.lhs} onChange={setLhs} baseTimeframe={baseTimeframe} compact={compact} />
        </div>
        <div className="text-center mt-6 text-sm font-mono text-textSecondary">{op === 'ADD' ? '+' : op === 'SUB' ? '-' : op === 'MUL' ? '×' : '÷'}</div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-textSecondary mb-1">Right</div>
          <OperandEditor value={v.rhs} onChange={setRhs} baseTimeframe={baseTimeframe} compact={compact} />
        </div>
      </div>
    </div>
  );
}
